import type { UseFormReturn } from 'react-hook-form';
import { Disclosure, DisclosureButton, Labeled, Text } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IApiAppPlatform } from '~/lib/api/modules/Apps';
import type { IArticleFormValues } from './ArticleForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { Link } from '~/components/atoms/Link';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { IapProductSelector } from '~/components/organisms/fields/IapProductSelector';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { getAppSettings } from '~/lib/appSettings';

export type IArticleFormMonetizationSectionProps = IContainerProps & {
  mode: IFormMode;
  form: UseFormReturn<IArticleFormValues>;
  appId: string;
};

export const ArticleFormMonetizationSection: React.FC<
  IArticleFormMonetizationSectionProps
> = (props) => {
  const { sx, form, appId } = props;
  const { t } = useTranslation();
  const appSettings = getAppSettings();
  const appQuery = useAppQuery({ appId });
  const compatiblePlatforms = appQuery.data?.builds
    ? Object.keys(appQuery.data.builds).filter(
        (platform) =>
          appQuery.data.builds?.[platform as IApiAppPlatform]?.iapEnabled,
      )
    : undefined;
  const iapEnabled = compatiblePlatforms && compatiblePlatforms.length > 0;

  return appQuery.isError ? (
    <ErrorMessage error={appQuery.error} />
  ) : appQuery.isLoading ? (
    <Disclosure
      sx={sx}
      trigger={
        <DisclosureButton loading>
          {t('ArticleFormMonetizationSection.price')}
        </DisclosureButton>
      }
    />
  ) : appQuery.data ? (
    <Disclosure
      sx={sx}
      initiallyExpanded={iapEnabled}
      trigger={
        <DisclosureButton>
          {t('ArticleFormMonetizationSection.price')}
        </DisclosureButton>
      }
    >
      <FormSection sub>
        <Labeled
          label={t('ArticleFormMonetizationSection.price-label')}
          disabled={!iapEnabled}
        >
          <FormField
            name="productId"
            control={form.control}
            as={IapProductSelector}
            forwardError
            productType="consumable"
            availableProductIds={appQuery?.data.settings?.iap?.products}
          />
        </Labeled>
        {compatiblePlatforms && compatiblePlatforms.length > 0 ? (
          <div>
            <Text variant="label" size="lg">
              {t('ArticleFormMonetizationSection.compatible-platforms')}
            </Text>{' '}
            {compatiblePlatforms
              ?.map((platform) => t(`common.${platform}`))
              .join(', ')}
          </div>
        ) : null}
        {!iapEnabled ? (
          <p>
            {t('error.iapNotEnabled')}{' '}
            <Link to={`mailto:${appSettings.contact.email}`}>
              {t('common.pleaseContactUs')}
            </Link>
            .
          </p>
        ) : null}
      </FormSection>
    </Disclosure>
  ) : null;
};
